import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import type { OrganizationInfo, UserMfaStatus } from "../../../api/clientsManagementTypes";
import { allUserMfaStatuses, userMfaStatusCaptions } from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import type { CompanyWithOrganization } from "../../common/CompanySelector";
import CompanySelector from "../../common/CompanySelector";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import PageTitle from "../../common/PageTitle";
import { buildCreateInvestorPortalUserMessage } from "./createInvestorPortalUserMessage";

const createInvestorPortalUserFromContact = withErrorHandling(api.usersManagement.createInvestorPortalUserFromContact);

interface CreateInvestorPortalUserTabProps {
  organizations: OrganizationInfo[];
}

const CreateInvestorPortalUserTab = ({ organizations }: CreateInvestorPortalUserTabProps) => {
  const { executionState, setExecutionState } = useExecutionState();
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithOrganization | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [emailValidationResult, setEmailValidationResult] = useState<ValidationResult>();
  const [mfaStatus, setMfaStatus] = useState<UserMfaStatus>("NoMFA");

  const handleCreateUser = async () => {
    if (!selectedCompany || !userEmail.trim()) {
      return;
    }

    const sanitizedEmail = sanitizeEmail(userEmail);
    const validationResult = emailValidator(sanitizedEmail);
    setEmailValidationResult(validationResult);
    if (!validationResult.isValid) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [resp, error] = await createInvestorPortalUserFromContact({
      email: sanitizedEmail,
      clientCode: selectedCompany.clientCode,
      mfaStatus,
    });

    if (error) {
      logError(error, "[CreateInvestorPortalUserTab] createInvestorPortalUserFromContact");
      setExecutionState({ status: "error", error });
      return;
    }

    setExecutionState(buildCreateInvestorPortalUserMessage(resp, mfaStatus));
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Create Investor Portal User" subtitle="🤦🏼‍♂ About this operation" defaultExpanded>
        Create an Investor Portal user for an existing contact
        <br />
        <br />
        <strong>When to use this:</strong>
        <br />
        • User cannot self-register on the portal
        <br />
        • User cannot receive verification emails with security codes
        <br />
        <br />
        💡 <strong>Tip:</strong> If the user has email delivery issues, select <strong>No MFA</strong> during creation.
        Note that users with <strong>No MFA</strong> will not receive verification codes at all.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <CompanySelector organizations={organizations} value={selectedCompany} onChange={setSelectedCompany} />
        <FieldHelp>Select the client company for the new user</FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="User Email"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          error={emailValidationResult?.isValid === false}
          helperText={emailValidationResult?.error}
        />
        <FieldHelp>
          Email address of an existing contact. This will be used as the login email and display name.
        </FieldHelp>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <FormControl fullWidth>
          <InputLabel>MFA Status</InputLabel>
          <Select label="MFA Status" value={mfaStatus} onChange={(e) => setMfaStatus(e.target.value as UserMfaStatus)}>
            {allUserMfaStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {userMfaStatusCaptions[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FieldHelp>
          <strong>No MFA:</strong> MFA disabled (no verification codes)
          <br />
          <strong>Conditional MFA:</strong> Default behavior (Azure AD policies)
          <br />
          <strong>Mandatory MFA:</strong> MFA required at every sign-in
        </FieldHelp>
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleCreateUser}
        loading={executionState.status === "in-progress"}
        disabled={!selectedCompany || !userEmail.trim()}
      >
        Create
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default CreateInvestorPortalUserTab;
